import { type PropsWithChildren, type ReactNode, useEffect, useState } from 'react'
import type { SyncRuntime } from './syncRuntime'

export interface DatabaseConfig {
  endpoint?: string
}

export interface DatabaseProviderProps {
  runtime: SyncRuntime<any, any>
  config?: DatabaseConfig
  fallback: (progress: number) => ReactNode
  error: (error: Error, reset: () => void) => ReactNode
}

export function SyncProvider({
  children,
  runtime,
  fallback,
  error: errorRenderer,
}: PropsWithChildren<DatabaseProviderProps>) {
  const [completed, setCompleted] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    const unlistenProgress = runtime.events.on('sync.init.progress', e => {
      if (e.progress == null) return
      setProgress(e.progress)
    })

    const unlistenInitFinished = runtime.events.on('sync.init.finished', _e => {
      setCompleted(true)
    })

    const unlistenInitError = runtime.events.on('sync.init.error', e => {
      setError(e.error)
    })

    void runtime
      .start()
      .then(() => setCompleted(true))
      .catch(err => {
        setError(err as Error)
      })

    return () => {
      unlistenProgress()
      unlistenInitFinished()
      unlistenInitError()
      runtime.stop()
    }
  }, [runtime])

  if (error) {
    return <>{errorRenderer(error, () => window.location.reload())}</>
  }

  if (!completed) {
    return <>{fallback(progress)}</>
  }

  return children
}
